import React from 'react'
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
} from 'recharts'
import { AnalysisHistoricalResults, ScoreEntry, ScoreData } from '../../types'
import usePadding from '../../hooks/useChartBarsPositions'
import { formatDate, getBarColor } from '../../utils/chartUtils'
import RenderCustomLegend from '../RenderCustomLegend'
import { useTranslation } from 'react-i18next'
interface EvolutionEmotionalBarChartProps {
  data: AnalysisHistoricalResults | null
  scoreKey: string
  lowLabel?: string
  mediumLabel?: string
  highLabel?: string
  yAxisLabels: string[]
  type: string
}

const EvolutionEmotionalBarChart: React.FC<EvolutionEmotionalBarChartProps> = ({
  data,
  scoreKey,
  lowLabel,
  mediumLabel,
  highLabel,
  type
}) => {
  const padding = usePadding()
  const { t } = useTranslation()

  const scoreData = data?.data?.attributes?.scores
    ?.find((s: ScoreData) => s.name === scoreKey)
    ?.data.map((item: ScoreEntry) => ({
      date: item.date,
      score: item.score,
      rating: Number(item.rating),
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  let minScore
  let maxScore

  switch (scoreKey) {
    case 'GOLDBERG_ANXIETY':
      minScore = 0
      maxScore = 4.2
      break
    case 'GOLDBERG_DEPRESSION':
      minScore = 0
      maxScore = 4.2
      break
    default:
      minScore = 0
      maxScore = 4.2
  }

  const validData = scoreData ? scoreData.slice(-3) : []

  if (!validData.length) {
    console.warn(`${scoreKey} no está disponible o no tiene datos.`)
  }

  const ticks = type === 'anxiety' ? [3, 4] : [1, 2]
  
  const yAxisLabelsWithDescriptions = t(
    'myEvolution.emotional.chart.yAxisLabels3',
    {
      returnObjects: true,
    },
  ) as string[]

  const yAxisLabels = yAxisLabelsWithDescriptions
  const axisLabels = ticks

  return (
    <div className="w-[100%] sm:w-[60%]">
      <ResponsiveContainer height={350}>
        <BarChart
          data={validData}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
        >
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            scale="point"
            padding={padding}
            axisLine={true}
            tickLine={true}
            tick={{ fontSize: 16 }}
          />

          <YAxis
            domain={[minScore, maxScore]}
            ticks={axisLabels}
            tickFormatter={(value) => {
              const index = axisLabels.indexOf(value)
              return yAxisLabels[index] !== undefined ? yAxisLabels[index] : ''
            }}
            interval={0}
            tickCount={axisLabels.length}
            width={100}
            tick={{ fontSize: 16 }}
          />

          {type === 'anxiety' ? (
            <>
              <ReferenceLine y={3} stroke="#0E8343" strokeDasharray="3 3" />
              <ReferenceLine y={4} stroke="#A52222" strokeDasharray="3 3" />
            </>
          ) : (
            <>
              <ReferenceLine y={1} stroke="#0E8343" strokeDasharray="3 3" />
              <ReferenceLine y={2} stroke="#A52222" strokeDasharray="3 3" />
            </>
          )}

          <Tooltip
            formatter={(value) => `${value} pts`}
            labelFormatter={formatDate}
          />
          <Legend
            content={
              <RenderCustomLegend
                lowLabel={lowLabel || ''}
                mediumLabel={mediumLabel || ''}
                highLabel={highLabel || ''}
              />
            }
          />

          <Bar
            dataKey="score"
            barSize={40}
            fillOpacity={1}
            stroke="none"
            name="Puntuación"
            minPointSize={2}
          >
            {validData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={getBarColor(entry)}
                style={{ visibility: entry.score.toString() === 'not available' ? 'hidden' : 'visible' }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default EvolutionEmotionalBarChart
